/**
 * Reading an API response body, for api.js's handleResponse.
 *
 * The body is read exactly once, with `response.text()`, and parsed as JSON
 * only when the response says it is JSON and has a body. That replaces a
 * `response.json()` that ran before any status check, which turned every
 * plain-text answer (Firebase Hosting's 500 "Internal Error", Cloud Run's 429
 * page, a limiter's text/plain 429) into `Unexpected token 'I'…` with no
 * status, made the fallback `response.text()` fail with "body stream already
 * read", and threw on an empty 204.
 *
 * Framework-free and pure, so it is tested on its own: api.js cannot be
 * imported under jest (it reads `import.meta`).
 */

/** Error bodies that are not the API's JSON: a 429 from Cloud Run or a limiter. */
export const BUSY_MESSAGE = 'The service is busy, please try again in a moment.';

/** Error bodies that are not the API's JSON: a 5xx page from the edge or the app. */
export const TEMPORARY_PROBLEM_MESSAGE = 'The service had a temporary problem, please try again.';

export const isJsonContentType = (contentType) => /json/i.test(String(contentType || ''));

const contentTypeOf = (response) =>
  (typeof response.headers?.get === 'function' ? response.headers.get('Content-Type') : null);

/**
 * The body, read once.
 *
 * @returns {Promise<{ data: any, isJson: boolean, text: string }>} `data` is
 *   the parsed JSON when the body is JSON, the raw text when it is not, and
 *   null when there is no body (a 204, or an empty 200).
 */
export const readResponseBody = async (response) => {
  if (response.status === 204 || response.status === 205) return { data: null, isJson: false, text: '' };
  const text = await response.text();
  if (!text.trim()) return { data: null, isJson: false, text };
  if (isJsonContentType(contentTypeOf(response))) {
    try {
      return { data: JSON.parse(text), isJson: true, text };
    } catch (error) {
      // Labelled JSON but not JSON: a success must not pass for one; an error
      // falls through to the plain-text message.
      if (response.ok) throw error;
    }
  }
  return { data: text, isJson: false, text };
};

/** A readable message for an error body that is not the API's JSON. */
export const plainErrorMessage = (status, text) => {
  if (status === 429) return BUSY_MESSAGE;
  if (status >= 500) return TEMPORARY_PROBLEM_MESSAGE;
  const message = String(text || '').trim();
  // A short plain-text reason is worth showing; an HTML page is not.
  if (message && message.length <= 200 && !message.startsWith('<')) return message;
  return `Request failed (${status})`;
};

/**
 * The Error for a failed response other than 401/403 (api.js handles those):
 * the API's JSON `error` or `message` when it sent one, else a readable
 * message. Always carries `status`; `data` when the body was JSON.
 */
export const responseError = (response, { data, isJson, text }) => {
  let message;
  if (isJson && data && (data.error || data.message)) message = data.error || data.message;
  else if (isJson) message = text || `Request failed (${response.status})`;
  else message = plainErrorMessage(response.status, text);
  const error = new Error(message);
  error.status = response.status;
  if (isJson) error.data = data;
  return error;
};
